import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import NotificationDropdown from '../shared/NotificationDropdown';
import logo from '../../assets/pictures/logo.png';
import {
  LayoutDashboard,
  Search,
  Users,
  FileText,
  DollarSign,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  MessageSquare
} from 'lucide-react';

const REP_MENU_ITEMS = [
  { path: '/representative', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/representative/students', icon: Users, label: 'My Students' },
  { path: '/representative/service-requests', icon: FileText, label: 'Service Requests' },
  { path: '/representative/commissions', icon: DollarSign, label: 'Commissions' },
  { path: '/representative/messages', icon: MessageSquare, label: 'Messages' }
];

export default function RepresentativeLayout({ children }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const repLevel = user?.role === 'rep1' ? 1 : 2;
  const commissionRate = repLevel === 1 ? '20%' : '40%';

  return (
    <div className="partner-dashboard relative flex h-screen overflow-hidden bg-[#f3f8ff]" data-testid="representative-layout">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-[#6FA8FF]/18 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex h-screen flex-col overflow-hidden border border-[#8DC7FF]/22 bg-[linear-gradient(180deg,rgba(255,255,255,0.78),rgba(232,243,255,0.72))] shadow-[0_18px_44px_rgba(111,168,255,0.14)] backdrop-blur-[22px] transition-all duration-300 lg:sticky lg:top-0 lg:z-20 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${collapsed ? 'w-20' : 'w-64'}`}
      >
        {/* Logo */}
        <div className={`flex items-center border-b border-[#8DC7FF]/18 px-4 py-4 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          <Link to="/representative" className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#8DC7FF]/24 bg-white/70 shadow-[0_14px_32px_rgba(111,168,255,0.16)] backdrop-blur-md">
              <img src={logo} alt="Fly8" className="h-7 w-auto object-contain" />
            </div>
            {!collapsed && (
              <div>
                <h1 className="text-xl font-outfit font-bold text-slate-900">Fly8</h1>
                <p className="text-[10px] font-medium uppercase tracking-[0.24em] text-[#4A8CF1]">Representative L{repLevel}</p>
              </div>
            )}
          </Link>

          <button
            className="hidden h-9 w-9 items-center justify-center rounded-xl border border-[#8DC7FF]/22 bg-white/55 text-slate-600 transition hover:border-[#8DC7FF]/34 hover:bg-white/78 lg:flex"
            onClick={() => setCollapsed(!collapsed)}
          >
            <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>

          <button
            className="rounded-xl border border-[#8DC7FF]/22 bg-white/55 p-2 text-slate-600 hover:bg-white/78 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Commission Badge */}
        {!collapsed && (
          <div className="mx-3 mt-3 rounded-2xl border border-[#8DC7FF]/22 bg-white/45 p-3">
            <p className="text-xs font-medium text-[#4A8CF1]">Commission Rate</p>
            <p className="text-2xl font-bold text-[#3F7EDC]">{commissionRate}</p>
          </div>
        )}

        {/* Menu */}
        <nav className="partner-scrollbar flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {REP_MENU_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path) && location.pathname !== '/representative';

            const finalActive = item.exact ? (location.pathname === item.path) : isActive;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center justify-between gap-3 rounded-2xl px-4 py-3 transition-all ${
                  finalActive
                    ? 'border border-[#6FA8FF]/48 bg-[linear-gradient(135deg,rgba(74,140,241,0.94),rgba(111,168,255,0.88))] text-white'
                    : 'border border-transparent text-slate-600 hover:border-[#8DC7FF]/18 hover:bg-white/45 hover:text-slate-900'
                } ${collapsed ? 'justify-center px-3' : ''}`}
                onClick={() => setSidebarOpen(false)}
                title={collapsed ? item.label : undefined}
              >
                <div className="flex items-center gap-3">
                  {finalActive && !collapsed && (
                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                  )}
                  <Icon className={`w-5 h-5 ${finalActive ? 'text-white' : 'text-slate-500'}`} />
                  {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
                </div>
                {!collapsed && item.badge && (
                  <Badge className={`min-w-[20px] px-1.5 py-0.5 text-center text-xs ${finalActive ? 'border-white/25 bg-white/18 text-white' : 'border-[#8DC7FF]/30 bg-[#6FA8FF]/14 text-[#3F7EDC]'}`}>
                    {item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className={`border-t border-[#8DC7FF]/18 p-4 ${collapsed ? 'flex flex-col items-center' : ''}`}>
          {!collapsed && (
            <div className="mb-3 flex items-center gap-3 rounded-2xl border border-[#8DC7FF]/18 bg-white/45 p-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[linear-gradient(135deg,#4A8CF1,#6FA8FF)] text-white font-semibold shadow-[0_12px_30px_rgba(111,168,255,0.2)]">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs font-medium text-[#4A8CF1]">Representative Level {repLevel}</p>
              </div>
            </div>
          )}
          <Button
            onClick={logout}
            variant="ghost"
            className={`w-full rounded-2xl border border-[#8DC7FF]/18 bg-white/40 text-slate-600 hover:bg-white/75 hover:text-slate-900 ${collapsed ? 'p-2' : 'justify-start'}`}
            size="sm"
          >
            <LogOut className={`w-4 h-4 ${collapsed ? '' : 'mr-2'}`} />
            {!collapsed && 'Logout'}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="relative z-10 flex min-w-0 flex-1 flex-col overflow-hidden bg-[#f3f8ff]">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 border-b border-[#8DC7FF]/22 bg-white px-4 py-4 shadow-[0_18px_40px_rgba(111,168,255,0.08)] lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                className="rounded-xl border border-[#8DC7FF]/22 bg-white/60 p-2 text-slate-700 hover:bg-white/78 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="hidden h-11 w-11 items-center justify-center rounded-2xl border border-[#8DC7FF]/22 bg-white/60 shadow-[0_12px_30px_rgba(111,168,255,0.12)] sm:flex">
                  <img src={logo} alt="Fly8" className="h-7 w-auto object-contain" />
                </div>
                <div>
                  <h2 className="text-xl font-outfit font-bold text-slate-900">Representative Dashboard</h2>
                  <p className="hidden text-sm text-slate-500 sm:block">Track your referrals and commissions</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A8CF1]" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-64 rounded-2xl border border-[#8DC7FF]/22 bg-white/74 py-2.5 pl-10 pr-4 text-sm text-slate-700 outline-none"
                />
              </div>
              <NotificationDropdown />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="partner-scrollbar flex-1 overflow-auto px-4 py-5 lg:px-8 lg:py-8">
          {children}
        </main>
      </div>
    </div>
  );
}
